import { ActionTree } from 'vuex'
import firebase from 'firebase'
import flatten from 'lodash/flatten'
import isEmpty from 'lodash/isEmpty'

import { RootState } from '../types'
import { ErrorMessage, StorageFileName } from '../../type'
import { UserState, User, UserUpdateEntity, UserIndex } from './types'
import {
  hasDataOperationAuthority,
  isUserAdmin,
  chunkIndicesUsers
} from '../../lib/appHelper'

const provider = new firebase.auth.GoogleAuthProvider()

const actions: ActionTree<UserState, RootState> = {
  signin: ({ commit }): Promise<User | undefined> => {
    return firebase
      .auth()
      .signInWithPopup(provider)
      .then(async res => {
        if (!res.user) {
          throw new Error(ErrorMessage.UserNotFound)
        }
        const db = firebase.firestore()
        const docRef = db.collection('users').doc(res.user.uid)
        const snapshot = await docRef.get()
        if (!snapshot.exists) throw new Error(ErrorMessage.UserNotFound)
        const unlisten = docRef.onSnapshot(doc => {
          commit('set', { user: doc.data() })
        })
        const user: User = snapshot.data() as User
        commit('set', { user, unlisten })
        return user
      })
  },

  signup: async ({ commit, dispatch }): Promise<User | undefined> => {
    return firebase
      .auth()
      .signInWithPopup(provider)
      .then(async res => {
        if (!res.user) {
          throw new Error(ErrorMessage.UserNotFound)
        }
        const db = firebase.firestore()
        const docRef = db.collection('users').doc(res.user.uid)
        const snapshot = await docRef.get()
        if (snapshot.exists) throw new Error(ErrorMessage.UserAlreadyExsists)
        const unlisten = docRef.onSnapshot(doc => {
          commit('set', { user: doc.data() })
        })
        const user: User = {
          id: res.user.uid,
          email: res.user.email as string,
          admin: false,
          createdAt: new Date(),
          updatedAt: null,
          recommended: null,
          familyName: null,
          firstName: null,
          imageUrl: null,
          imageUrl2: null,
          profile: null,
          specialtyType: null,
          featureTypes: null
        }
        await dispatch('transactionUpsert', user)
        commit('set', { user, unlisten })
        return user
      })
  },

  signout: ({ commit }): void => {
    firebase
      .auth()
      .signOut()
      .then(() => {
        commit('signout')
      })
  },

  autoSignin: async ({ getters, commit, dispatch }): Promise<undefined> => {
    const db = firebase.firestore()
    const currentUser = firebase.auth().currentUser
    commit('loading')
    if (currentUser) {
      if (getters.signedIn) {
        commit('loading')
        return
      }
      const unlisten = db
        .collection('users')
        .doc(currentUser.uid)
        .onSnapshot(doc => {
          commit('set', { user: doc.data() })
        })
      const userData = await dispatch('get', currentUser.uid)
      if (userData) commit('set', { user: userData, unlisten })
      commit('loaded')
      return
    }
    return new Promise(resolve =>
      firebase.auth().onAuthStateChanged(async user => {
        if (!user) {
          commit('signout')
          commit('loaded')
          return resolve()
        }
        const unlisten = db
          .collection('users')
          .doc(user.uid)
          .onSnapshot(doc => {
            commit('set', { user: doc.data() })
          })
        const userData = await dispatch('get', user.uid)
        if (userData) commit('set', { user: userData, unlisten })
        commit('loaded')
        resolve()
      })
    )
  },

  get: async (_, userId): Promise<User | undefined> => {
    const user = firebase.auth().currentUser
    if (!user) return
    const db = firebase.firestore()
    const ref = db.collection('users').doc(userId)
    const snapshot = await ref.get()
    if (!snapshot.exists) return
    return snapshot.data() as User
  },

  update: async (
    { dispatch },
    { user, image, image2 }: UserUpdateEntity
  ): Promise<void> => {
    if (!hasDataOperationAuthority(user.id)) return
    const [imageUrl, imageUrl2] = await Promise.all([
      image
        ? await dispatch('uploadImage', {
            image,
            name: StorageFileName.Profile
          })
        : image === null
        ? dispatch('deleteImage', {
            userId: user.id,
            name: StorageFileName.Profile
          })
        : Promise.resolve(),
      image2
        ? await dispatch('uploadImage', {
            image: image2,
            name: StorageFileName.Profile2
          })
        : image2 === null
        ? dispatch('deleteImage', {
            userId: user.id,
            name: StorageFileName.Profile2
          })
        : Promise.resolve()
    ])
    const newUser: User = { ...user, updatedAt: new Date() }
    if (image !== undefined) newUser.imageUrl = imageUrl || null
    if (image2 !== undefined) newUser.imageUrl2 = imageUrl2 || null
    // サブ画像しかない場合、メイン昇格
    if (newUser.imageUrl2 && !newUser.imageUrl) {
      newUser.imageUrl = newUser.imageUrl2
      newUser.imageUrl2 = null
    }
    await dispatch('transactionUpsert', newUser)
  },

  delete: async ({ dispatch }, userIds: string[]): Promise<void> => {
    if (!isUserAdmin()) return
    const db = firebase.firestore()
    const userIndices: UserIndex[] = await dispatch('fetchIndices')
    const version = isEmpty(userIndices) ? 0 : userIndices[0].version + 1
    const users = flatten(userIndices.map(index => index.data))
    const newUsers = users.filter(u => !userIds.includes(u.id))
    const chunkUsers = chunkIndicesUsers(newUsers)
    const newUserIndices =
      userIndices.length > chunkUsers.length
        ? userIndices.map((_, i) =>
            i < chunkUsers.length ? { data: chunkUsers[i] } : { data: [] }
          )
        : chunkUsers.map(users => ({ data: users }))
    await db.runTransaction(transaction => {
      return Promise.all([
        ...userIds.map(id =>
          transaction.delete(db.collection('users').doc(id))
        ),
        ...newUserIndices.map((index, i) => {
          const ref = db.collection('indices').doc(`users_${i}`)
          return index.data.length > 0
            ? transaction.update(ref, { data: index.data, version })
            : transaction.delete(ref)
        }),
        ...userIds.map(id => dispatch('deleteImage', { userId: id }))
      ])
    })
    return
  },

  transactionUpsert: async ({ dispatch }, user: User): Promise<void> => {
    const db = firebase.firestore()
    // users
    const userRef = db.collection('users').doc(user.id)
    const userSnapshot = await userRef.get()
    // indices
    const userIndices: UserIndex[] = await dispatch('fetchIndices')
    const version = isEmpty(userIndices) ? 0 : userIndices[0].version + 1
    const users = flatten(userIndices.map(index => index.data))
    const newUsers = users.find(i => i.id === user.id)
      ? users.map(u => (u.id === user.id ? user : u))
      : [...users, user]
    const chunkUsers = chunkIndicesUsers(newUsers)
    const newUserIndices =
      userIndices.length > chunkUsers.length
        ? userIndices.map((_, i) =>
            i < chunkUsers.length ? { data: chunkUsers[i] } : { data: [] }
          )
        : chunkUsers.map(users => ({ data: users }))
    await db.runTransaction(transaction => {
      return Promise.all([
        transaction[userSnapshot.exists ? 'update' : 'set'](userRef, user),
        ...newUserIndices.map(async (index, i) => {
          const ref = db.collection('indices').doc(`users_${i}`)
          const snapshot = await ref.get()
          return isEmpty(index.data)
            ? transaction.delete(ref)
            : transaction[snapshot.exists ? 'update' : 'set'](ref, {
                version,
                data: index.data
              })
        })
      ])
    })
  },

  updateRecommend: async (
    { dispatch },
    { userId, recommended }: { userId: string; recommended: boolean }
  ): Promise<void> => {
    if (!hasDataOperationAuthority(userId)) return
    const db = firebase.firestore()
    const userRef = db.collection('users').doc(userId)
    const snapshot = await userRef.get()
    const user = snapshot.data() as User
    await dispatch('transactionUpsert', {
      ...user,
      recommended
    })
  },

  uploadImage: async (
    { state },
    { image, name }: { image: Blob; name: string }
  ): Promise<string | undefined> => {
    const user = state.data.user
    if (!user) return
    const storageRef = firebase.storage().ref()
    const imageRef = storageRef.child(`${user.id}/${name}`)
    const snapshot = await imageRef.put(image, {
      cacheControl: 'public,max-age=86400',
      contentType: 'image/png'
    })
    return snapshot.ref.getDownloadURL()
  },

  deleteImage: async (
    _,
    { userId, name }: { userId: string; name: StorageFileName }
  ): Promise<void> => {
    if (!hasDataOperationAuthority(userId)) return
    const storageRef = firebase.storage().ref()
    if (!name) {
      await Promise.all(
        Object.values(StorageFileName).map(name =>
          storageRef.child(`${userId}/${name}`).delete()
        )
      ).catch(() => {
        // do nothing.
      })
      return
    }
    const ref = storageRef.child(`${userId}/${name}`)
    await ref.delete().catch(() => {
      // do nothing.
    })
  },

  fetchAll: async ({ dispatch }): Promise<User[]> => {
    const userIndices: UserIndex[] = await dispatch('fetchIndices')
    return flatten(userIndices.map(index => index.data))
  },

  fetchIndices: async (): Promise<UserIndex[]> => {
    const db = firebase.firestore()
    const snapshot = await db.collection('indices').get()
    if (snapshot.empty) return []
    return snapshot.docs.map(doc => doc.data() as UserIndex)
  }
}

export default actions
